import * as React from "react";
import * as _ from "lodash";
import { useRef, useMemo, useEffect, useCallback, useState, FC } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { AutoSizer } from "react-virtualized";
import { InfiniteLoader } from "react-virtualized/dist/commonjs/index";
import "react-virtualized/styles.css";
import { Grid } from "react-virtualized/dist/commonjs/Grid";
import { MediaLightBox } from "./media-lightbox";
import { IUnlinkedImage, IUnlinkedMedia, IUnlinkedThreedium, IUnlinkedVideo } from "constants/unlinked-media";
import Image from "components/image";
import Checkbox from "@material-ui/core/Checkbox";
import theme from "styles/theme";
import { IVideoRef, VideoPlayer } from "./video-player";
import { MEDIA_TYPE_IMAGE, MEDIA_TYPE_THREEDIUM, MEDIA_TYPE_VIDEO } from "constants/media";
import type { IScope } from "reducers/scope";
import { format } from "lib/globalize-util";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import ZoomIcon from "@material-ui/icons/ZoomIn";
import { InlineLoading } from "components/content-loading";
import { DATATEST } from "constants/integration";
import Image3DIcon from "../icons/image-3d";
import { blurFix } from "styles/mixins";

interface IProps {
    scope: IScope;
    unlinkedMedia: IUnlinkedMedia[];
    isFetching: boolean;
    selectedMedia: IUnlinkedMedia[];
    onSelectedMediaUpdate: (selectedMedia: IUnlinkedMedia) => () => void;
    onFetchNext: () => void;
}

const ITEM_WIDTH = 220;
const ITEM_HEIGHT = 352;
const DEFAULT_COLUMN_COUNT = 4;

export const ManageUnlinkedTable: FC<IProps> = (props: IProps) => {
    const { unlinkedMedia, isFetching, scope } = props;
    const { selectedMedia, onSelectedMediaUpdate, onFetchNext } = props;
    const classes = useStyles();

    const player = useRef<IVideoRef>(null);
    const gridRef = useRef<Grid>();
    const [columnCount, setColumnCount] = useState<number>(DEFAULT_COLUMN_COUNT);
    const [lightBoxOpen, setLightBoxOpen] = useState<boolean>(false);
    const [magnifiedMedia, setMagnifiedMedia] = useState<any>();

    useEffect(() => {
        if (gridRef.current) {
            gridRef.current.recomputeGridSize();
        }
        setColumnCount(Math.min(unlinkedMedia.length, DEFAULT_COLUMN_COUNT));
    }, [unlinkedMedia]);

    const imageFallback = useMemo(() => {
        const brandImageSrc = _.get(scope, "brand.settings.missing_logo_image", "");
        return `//cdn1.nuorder.com/brands/${brandImageSrc}.jpg`;
    }, [scope]);

    const handleLightBox = useCallback(
        (unlinkedMediaItem?: IUnlinkedMedia) => (event: React.SyntheticEvent<unknown>) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();

            const isOpen = !!unlinkedMediaItem;
            const restrictImageDownload = scope.brand.hasPreference("restrict_image_download");
            if (isOpen && player && player.current) {
                player.current.pause();
            }
            setLightBoxOpen(isOpen);
            const mediaSources = unlinkedMediaItem ? getMediaSources(unlinkedMediaItem, restrictImageDownload) : {};
            setMagnifiedMedia(isOpen ? mediaSources : null);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [player, imageFallback]
    );

    const isRowLoaded = useCallback(({ index }) => !!unlinkedMedia[index], [unlinkedMedia]);

    const noContentRenderer = useCallback(
        () =>
            isFetching ? (
                <InlineLoading className={classes.loading} />
            ) : (
                <div data-test={DATATEST.MEDIA.NO_RESULTS_MESSAGE} className={classes.noResults}>
                    {format("No results found")}
                </div>
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isFetching]
    );

    const cellRenderer = useCallback(
        ({ columnIndex, rowIndex, key, style }) => {
            const unlinkedMediaItem = unlinkedMedia[rowIndex * columnCount + columnIndex];
            const isChecked = !!_.find(selectedMedia, (item) => item.id === unlinkedMediaItem?.id);
            return unlinkedMediaItem ? (
                <div key={key} style={style} className={classes.gridItemContainer}>
                    <Checkbox
                        data-test={DATATEST.MEDIA.UNLINKED.ADD_CHECKBOX}
                        className={classes.checkbox}
                        onClick={onSelectedMediaUpdate(unlinkedMediaItem)}
                        checked={isChecked}
                    />
                    <div
                        data-test={DATATEST.MEDIA.UNLINKED.UNLINKED_MEDIA_SOURCE_CONTAINER}
                        className={classes.mediaContainer}
                    >
                        {unlinkedMediaItem.type === MEDIA_TYPE_IMAGE && (
                            <Image
                                src={[unlinkedMediaItem.lowResolutionSource, imageFallback]}
                                className={classes.image}
                                alt="Unlinked media"
                            />
                        )}
                        {unlinkedMediaItem.type === MEDIA_TYPE_VIDEO && (
                            <VideoPlayer
                                ref={player}
                                source={unlinkedMediaItem.lowResolutionSource}
                                poster={unlinkedMediaItem.lowResolutionPoster}
                                lightMode={true}
                            />
                        )}
                        {unlinkedMediaItem?.type === MEDIA_TYPE_THREEDIUM && (
                            <div className={classes.iconContainer}>
                                <Image3DIcon className={classes.icon} />
                            </div>
                        )}
                    </div>
                    <div className={classes.tip}>
                        <div data-test={DATATEST.MEDIA.UNLINKED.FILE_NAME}>{unlinkedMediaItem.filename}</div>
                        <Tooltip
                            title={<span className={classes.tooltip}>{format("Click to zoom")}</span>}
                            placement="top-end"
                        >
                            <IconButton
                                data-test={DATATEST.MEDIA.UNLINKED.ZOOM}
                                className={classes.zoomButton}
                                onClick={handleLightBox(unlinkedMediaItem)}
                            >
                                <ZoomIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            ) : null;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [unlinkedMedia, columnCount, selectedMedia, imageFallback]
    );

    const handleSectionRendered = useCallback(
        (onRowsRendered) =>
            ({ columnStartIndex, columnStopIndex, rowStartIndex, rowStopIndex }) => {
                const startIndex = rowStartIndex * columnCount + columnStartIndex;
                const stopIndex = rowStopIndex * columnCount + columnStopIndex;
                onRowsRendered({ startIndex, stopIndex });
            },
        [columnCount]
    );

    const loadMoreRows = useCallback(() => {
        onFetchNext();
        return null;
    }, [onFetchNext]);

    const rowCount = useMemo(() => Math.ceil(unlinkedMedia?.length / DEFAULT_COLUMN_COUNT) || 1, [unlinkedMedia]);

    return (
        <>
            <div data-test={DATATEST.MEDIA.UNLINKED.TABLE_ROOT} className={classes.autoSizerRoot}>
                <InfiniteLoader isRowLoaded={isRowLoaded} loadMoreRows={loadMoreRows} rowCount={10000} threshold={10}>
                    {({ onRowsRendered }) => (
                        <AutoSizer>
                            {({ height, width }) => (
                                <Grid
                                    ref={gridRef}
                                    className={classes.gridContainer}
                                    cellRenderer={cellRenderer}
                                    columnCount={columnCount}
                                    columnWidth={ITEM_WIDTH}
                                    height={height}
                                    rowCount={rowCount}
                                    noContentRenderer={noContentRenderer}
                                    rowHeight={ITEM_HEIGHT}
                                    width={width}
                                    overscanRowCount={2}
                                    onSectionRendered={handleSectionRendered(onRowsRendered)}
                                />
                            )}
                        </AutoSizer>
                    )}
                </InfiniteLoader>
            </div>
            <MediaLightBox open={lightBoxOpen} media={[magnifiedMedia]} currentIndex={0} onClose={handleLightBox()} />
        </>
    );
};

const getMediaSources = (unlinkedMediaItem: IUnlinkedMedia, restrictImageDownload: boolean) => {
    const sources = {
        [MEDIA_TYPE_IMAGE]: { source: (unlinkedMediaItem as IUnlinkedImage).highResolutionSource },
        [MEDIA_TYPE_VIDEO]: {
            source: (unlinkedMediaItem as IUnlinkedVideo).highResolutionSource,
            poster: (unlinkedMediaItem as IUnlinkedVideo).highResolutionPoster,
        },
        [MEDIA_TYPE_THREEDIUM]: { threedium_id: (unlinkedMediaItem as IUnlinkedThreedium).threedium_id },
    };
    return {
        type: unlinkedMediaItem.type,
        download: !restrictImageDownload ? `${unlinkedMediaItem.url}?max_dimension=4000&download=true` : null,
        ...sources[unlinkedMediaItem.type],
    };
};

const useStyles = makeStyles({
    gridContainer: {
        overflowX: "hidden !important" as any,
    },
    gridItemContainer: {
        padding: "5px",
        "& img": {
            height: "260px",
            width: "100%",
            objectFit: "contain",
        },
    },
    autoSizerRoot: {
        minHeight: "370px",
    },
    mediaContainer: {
        height: "260px",
    },
    image: {
        ...blurFix,
    },
    loading: {
        margin: "40px 0 0 0",
    },
    helper: {
        zIndex: "1500 !important" as any,
    },
    noResults: {
        textAlign: "center",
        maxWidth: 450,
        fontSize: 20,
        padding: "20px 10px",
        margin: "auto",
    },
    checkbox: {
        backgroundColor: "transparent !important",
        color: theme.palette.grey,
        "& .MuiSvgIcon-root": {
            fontSize: 24,
        },
        "&.Mui-checked": {
            color: theme.palette.primary1Color,
        },
    },
    tip: {
        marginTop: 13,
        fontSize: 10,
        textAlign: "center",
        position: "relative",
    },
    tooltip: {
        fontSize: 12,
        lineHeight: "18px",
    },
    iconContainer: {
        height: "100%",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
    },
    icon: {
        width: "50%",
        height: "50%",
        color: theme.palette.grey,
    },
    zoomButton: {
        color: theme.palette.darkerGrey,
        position: "absolute",
        top: -7,
        right: 10,
        zIndex: 1000,
        transform: "translateY(-50%)",
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.7)",
        "&:hover": {
            backgroundColor: "rgba(245,245,245,0.85)",
        },
    },
});
